/**
 * Marketplace Tab - Browse and Install Plugins (Ant Design v5)
 * 
 * TASK-F1.4: Complete marketplace browsing and installation ✅
 */

import React, { useState, useEffect } from 'react';
import {
  Input,
  Button,
  Tag,
  Avatar,
  Row,
  Col,
  Card,
  Divider,
  Select,
  Skeleton,
  Modal,
  Alert,
  Tabs,
  Typography,
  Space,
  message
} from 'antd';
import {
  SearchOutlined,
  CloseOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import { navigateToTab } from '../utils/navigation';

const { Title, Text, Paragraph } = Typography;

interface MarketplacePlugin {
  id: string;
  name: string;
  version: string;
  description: string;
  author: string;
  icon: string;
  category: string;
  installations: number;
  tags: string[];
  screenshots: string[];
  publishedDate: string;
  lastUpdated: string;
  isInstalled: boolean;
  isCertified: boolean;
  publisherType: 'verified' | 'partner' | 'community';
}

interface Category {
  id: string;
  name: string;
  icon: string;
  count: number;
}

const MarketplaceTab: React.FC = () => {
  const [plugins, setPlugins] = useState<MarketplacePlugin[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('popular');
  const [installDialogOpen, setInstallDialogOpen] = useState(false);
  const [selectedPlugin, setSelectedPlugin] = useState<MarketplacePlugin | null>(null);
  const [installing, setInstalling] = useState(false);

  // Fetch marketplace data
  useEffect(() => {
    fetchMarketplaceData();
  }, []);

  const fetchMarketplaceData = async () => {
    setLoading(true);
    
    // TODO: Replace with actual API calls
    await new Promise(resolve => setTimeout(resolve, 700));
    
    // Mock categories
    const mockCategories: Category[] = [
      { id: 'all', name: 'All Apps', icon: '🔍', count: 24 },
      { id: 'analytics', name: 'Analytics', icon: '📊', count: 12 },
      { id: 'automation', name: 'Automation', icon: '⚙️', count: 8 },
      { id: 'communication', name: 'Communication', icon: '💬', count: 6 },
      { id: 'productivity', name: 'Productivity', icon: '⚡', count: 15 },
      { id: 'ai', name: 'AI & ML', icon: '🤖', count: 9 },
      { id: 'utilities', name: 'Utilities', icon: '🔧', count: 11 },
      { id: 'integration', name: 'Integrations', icon: '🔗', count: 7 }
    ];
    
    // Mock marketplace plugins
    const mockPlugins: MarketplacePlugin[] = [
      {
        id: 'advanced-analytics-pro',
        name: 'Advanced Analytics Pro',
        version: '2.5.0',
        description: 'Enterprise analytics suite with custom dashboards, real-time metrics, and AI-powered insights. Transform your data into actionable intelligence.',
        author: 'EmpowerID',
        icon: '📊',
        category: 'analytics',
        installations: 1523,
        tags: ['analytics', 'dashboards', 'reporting', 'charts'],
        screenshots: [],
        publishedDate: '2024-08-15',
        lastUpdated: '2024-10-20',
        isInstalled: false,
        isCertified: true,
        publisherType: 'verified'
      },
      {
        id: 'workflow-automation',
        name: 'Workflow Automation Engine',
        version: '3.1.2',
        description: 'Build and automate complex business workflows with drag-and-drop interface and triggers. Streamline repetitive tasks and boost productivity.',
        author: 'EmpowerID',
        icon: '⚙️',
        category: 'automation',
        installations: 2341,
        tags: ['automation', 'workflow', 'integration', 'triggers'],
        screenshots: [],
        publishedDate: '2024-07-01',
        lastUpdated: '2024-10-25',
        isInstalled: false,
        isCertified: true,
        publisherType: 'verified'
      },
      {
        id: 'team-collaboration-hub',
        name: 'Team Collaboration Hub',
        version: '1.8.0',
        description: 'Enterprise collaboration tools including secure messaging, video conferencing, and shared workspaces. Keep your team connected and productive.',
        author: 'Collaboration Partners',
        icon: '👥',
        category: 'communication',
        installations: 987,
        tags: ['collaboration', 'chat', 'video', 'teamwork'],
        screenshots: [],
        publishedDate: '2024-09-10',
        lastUpdated: '2024-10-18',
        isInstalled: false,
        isCertified: false,
        publisherType: 'partner'
      },
      {
        id: 'data-visualization-suite',
        name: 'Data Visualization Suite',
        version: '2.0.1',
        description: 'Create professional visualizations with 50+ chart types and interactive dashboards. Make your data insights clear and actionable.',
        author: 'DataViz Solutions',
        icon: '📈',
        category: 'analytics',
        installations: 1876,
        tags: ['visualization', 'charts', 'graphs', 'dashboards'],
        screenshots: [],
        publishedDate: '2024-06-20',
        lastUpdated: '2024-10-22',
        isInstalled: false,
        isCertified: true,
        publisherType: 'partner'
      },
      {
        id: 'ai-content-generator',
        name: 'AI Content Generator',
        version: '1.5.3',
        description: 'Generate high-quality content using advanced AI models. Perfect for business documentation, communications, and content creation.',
        author: 'AI Solutions',
        icon: '🤖',
        category: 'ai',
        installations: 3210,
        tags: ['ai', 'content', 'generation', 'writing'],
        screenshots: [],
        publishedDate: '2024-05-15',
        lastUpdated: '2024-10-27',
        isInstalled: false,
        isCertified: true,
        publisherType: 'partner'
      },
      {
        id: 'smart-notifications',
        name: 'Smart Notifications Pro',
        version: '2.2.0',
        description: 'Enterprise notification management with priority filtering, intelligent grouping, and custom rules. Never miss critical updates.',
        author: 'EmpowerID',
        icon: '🔔',
        category: 'communication',
        installations: 1654,
        tags: ['notifications', 'alerts', 'smart', 'priority'],
        screenshots: [],
        publishedDate: '2024-07-20',
        lastUpdated: '2024-10-15',
        isInstalled: false,
        isCertified: true,
        publisherType: 'verified'
      },
      {
        id: 'pdf-toolkit',
        name: 'PDF Toolkit Enterprise',
        version: '3.0.5',
        description: 'Complete PDF solution: create, edit, merge, split, and convert PDFs. Enterprise-grade document management.',
        author: 'DocTools Enterprise',
        icon: '📄',
        category: 'utilities',
        installations: 2890,
        tags: ['pdf', 'documents', 'conversion', 'editing'],
        screenshots: [],
        publishedDate: '2024-04-10',
        lastUpdated: '2024-10-12',
        isInstalled: false,
        isCertified: false,
        publisherType: 'partner'
      },
      {
        id: 'task-manager-deluxe',
        name: 'Enterprise Task Manager',
        version: '2.7.1',
        description: 'Comprehensive task and project management with Kanban boards, Gantt charts, and team collaboration features.',
        author: 'Productivity Solutions',
        icon: '✅',
        category: 'productivity',
        installations: 4123,
        tags: ['tasks', 'projects', 'kanban', 'productivity'],
        screenshots: [],
        publishedDate: '2024-03-05',
        lastUpdated: '2024-10-18',
        isInstalled: true,
        isCertified: true,
        publisherType: 'partner'
      }
    ];
    
    setCategories(mockCategories);
    setPlugins(mockPlugins);
    setLoading(false);
  };

  // Filter and sort plugins
  const filteredPlugins = plugins
    .filter(plugin => {
      // Category filter
      if (selectedCategory !== 'all' && plugin.category !== selectedCategory) {
        return false;
      }
      
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          plugin.name.toLowerCase().includes(query) ||
          plugin.description.toLowerCase().includes(query) ||
          plugin.author.toLowerCase().includes(query) ||
          plugin.tags.some(tag => tag.toLowerCase().includes(query))
        );
      }
      
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'popular':
          return b.installations - a.installations;
        case 'certified':
          if (a.isCertified && !b.isCertified) return -1;
          if (!a.isCertified && b.isCertified) return 1;
          return b.installations - a.installations;
        case 'newest':
          return new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime();
        case 'updated':
          return new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime();
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

  const handleInstallClick = (plugin: MarketplacePlugin) => {
    setSelectedPlugin(plugin);
    setInstallDialogOpen(true);
  };

  const handleInstallConfirm = async () => {
    if (!selectedPlugin) return;
    
    setInstalling(true);
    
    // TODO: Call actual install API
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Update local state
    setPlugins(prev =>
      prev.map(p =>
        p.id === selectedPlugin.id ? { ...p, isInstalled: true } : p
      )
    );
    
    setInstalling(false);
    setInstallDialogOpen(false);
    setSelectedPlugin(null);
    message.success('Plugin installed successfully!');
  };

  const handleInstallCancel = () => {
    setInstallDialogOpen(false);
    setSelectedPlugin(null);
  };

  const categoryTabs = categories.map(category => ({
    key: category.id,
    label: (
      <Space>
        <span>{category.icon}</span>
        <span>{category.name}</span>
        <Tag>{category.count}</Tag>
      </Space>
    )
  }));

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <Title level={3}>Apps Library</Title>
        <Text type="secondary">
          Discover and build new apps to expand platform capabilities
        </Text>
      </div>

      {/* Category Tabs */}
      <Card style={{ marginBottom: 24 }} bodyStyle={{ padding: 0 }}>
        <Tabs
          activeKey={selectedCategory}
          onChange={(key) => setSelectedCategory(key)}
          items={categoryTabs}
        />
      </Card>

      {/* Search and Sort Bar */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col xs={24} md={16}>
            <Input
              placeholder="Search apps by name, description, publisher, or capabilities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              prefix={<SearchOutlined />}
              suffix={
                searchQuery && (
                  <CloseOutlined
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSearchQuery('')}
                  />
                )
              }
            />
          </Col>
          <Col xs={24} md={8}>
            <Select
              value={sortBy}
              onChange={(value) => setSortBy(value)}
              style={{ width: '100%' }}
              options={[
                { label: 'Most Installed', value: 'popular' },
                { label: 'Certified First', value: 'certified' },
                { label: 'Newest', value: 'newest' },
                { label: 'Recently Updated', value: 'updated' },
                { label: 'Name (A-Z)', value: 'name' }
              ]}
            />
          </Col>
        </Row>
      </Card>

      {/* Results Count */}
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">
          Showing {filteredPlugins.length} app{filteredPlugins.length !== 1 ? 's' : ''}
          {searchQuery && ` matching "${searchQuery}"`}
        </Text>
      </div>

      {/* Plugin Grid */}
      {loading ? (
        <Row gutter={[16, 16]}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Col xs={24} md={12} lg={8} key={i}>
              <Card>
                <Skeleton active />
              </Card>
            </Col>
          ))}
        </Row>
      ) : filteredPlugins.length === 0 ? (
        <Card>
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <Title level={5} type="secondary">
              🔍 No apps found
            </Title>
            <Text type="secondary">
              {searchQuery
                ? `No apps match "${searchQuery}". Try a different search term.`
                : 'No apps available in this category.'}
            </Text>
            {searchQuery && (
              <div style={{ marginTop: 24 }}>
                <Button onClick={() => setSearchQuery('')}>
                  Clear Search
                </Button>
              </div>
            )}
          </div>
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {filteredPlugins.map((plugin) => (
            <Col xs={24} md={12} lg={8} key={plugin.id}>
              <Card
                hoverable
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                {/* Plugin Header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
                  <Avatar
                    size={56}
                    style={{ fontSize: '2rem', backgroundColor: '#e6f7ff' }}
                  >
                    {plugin.icon}
                  </Avatar>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                      <Title level={5} style={{ margin: 0 }}>
                        {plugin.name}
                      </Title>
                      {plugin.isCertified && (
                        <Tag icon={<SafetyCertificateOutlined />} color="blue">Certified</Tag>
                      )}
                      {plugin.publisherType === 'verified' && (
                        <Tag color="green">Verified Publisher</Tag>
                      )}
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      v{plugin.version} • {plugin.author}
                    </Text>
                  </div>
                </div>

                {/* Description */}
                <Paragraph type="secondary" style={{ marginBottom: 16, flex: 1 }}>
                  {plugin.description}
                </Paragraph>

                {/* Tags */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 16 }}>
                  {plugin.tags.slice(0, 3).map(tag => (
                    <Tag key={tag}>{tag}</Tag>
                  ))}
                  {plugin.tags.length > 3 && (
                    <Tag>+{plugin.tags.length - 3}</Tag>
                  )}
                </div>

                <Divider style={{ margin: '16px 0' }} />

                {/* Stats */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {plugin.installations.toLocaleString()} installations
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Updated {new Date(plugin.lastUpdated).toLocaleDateString()}
                  </Text>
                </div>

                {/* Action Button */}
                {plugin.isInstalled ? (
                  <Button
                    block
                    icon={<CheckCircleOutlined />}
                    onClick={() => navigateToTab('installed')}
                  >
                    Already Installed
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    block
                    icon={<DownloadOutlined />}
                    onClick={() => handleInstallClick(plugin)}
                  >
                    Install App
                  </Button>
                )}
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Install Confirmation Dialog */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Avatar style={{ fontSize: '1.5rem' }}>{selectedPlugin?.icon}</Avatar>
            <div>
              <div>Install App?</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {selectedPlugin?.name} v{selectedPlugin?.version}
              </Text>
            </div>
          </div>
        }
        open={installDialogOpen}
        onOk={handleInstallConfirm}
        onCancel={handleInstallCancel}
        okText={installing ? 'Installing...' : 'Install'}
        okButtonProps={{ loading: installing, icon: <DownloadOutlined /> }}
        width={600}
      >
        <Alert
          message="This app will be installed and enabled immediately for your organization."
          type="info"
          style={{ marginBottom: 16 }}
        />
        <div>
          <Title level={5}>{selectedPlugin?.name}</Title>
          <Paragraph type="secondary">
            {selectedPlugin?.description}
          </Paragraph>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                Publisher
              </Text>
              <Text>{selectedPlugin?.author}</Text>
            </Col>
            <Col span={8}>
              <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                Version
              </Text>
              <Text>{selectedPlugin?.version}</Text>
            </Col>
            <Col span={8}>
              <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                Status
              </Text>
              <Text>
                {selectedPlugin?.isCertified && <><SafetyCertificateOutlined /> Certified</>}
                {!selectedPlugin?.isCertified && <>Unverified</>}
              </Text>
            </Col>
          </Row>
          {selectedPlugin && !selectedPlugin.isCertified && (
            <Alert
              message="This app is not certified. Please review security and compliance requirements before installing."
              type="warning"
            />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default MarketplaceTab;

